import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Navbar from "../../Header/header";
import SASidebar from "../../Dashboard/SuperAdmin/Superadmincom/SuperAdminSidebar";
import { API_LoginURL } from "../../../config";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import { ClipLoader } from "react-spinners";
import Select from "react-select";
import { debounce, filter } from "lodash";

export default function ProjectMaster() {
    // State management
    const [state, setState] = useState({
        courses: [],
        projects: [],
        loading: true,
        formSubmitting: false,
        formData: {
            projectId: 0,
            courseId: 0,
            projectName: "",
            projectDescription: "",
            projectStatus: "Active",
            projectStartDate: "",
            projectEndDate: "",
        },
        searchQuery: "",
        filterStatus: "all",
        currentPage: 0,
        itemsPerPage: 5,
    });

    useEffect(() => {
        console.log("state ===== ", state);
    }, [state]);

    // Helper function to update state
    const updateState = (newState) => {
        setState((prev) => ({ ...prev, ...newState }));
    };

    // Fetch data on component mount
    useEffect(() => {
        fetchProjects();
        fetchCourses();
    }, []);

    // API calls
    const fetchCourses = async () => {
        try {
            const response = await axios.get(
                `${API_LoginURL}Course/SuperAdminCourseMasterList`,
                {
                    headers: getHeaders(),
                }
            );
            updateState({
                courses: Array.isArray(response.data) ? response.data : [],
            });
        } catch (error) {
            console.error("Error fetching courses:", error);
            Swal.fire("Error!", "Failed to load courses.", "error");
        }
    };

    const fetchProjects = async () => {
        updateState({ loading: true });
        try {
            const response = await axios.get(
                `${API_LoginURL}project/ProjectMasterList`,
                {
                    headers: getHeaders(),
                }
            );
            updateState({
                projects: Array.isArray(response.data) ? response.data : [],
                loading: false,
            });
        } catch (error) {
            console.error("Error fetching projects:", error);
            Swal.fire("Error!", "Failed to load projects.", "error");
            updateState({ loading: false });
        }
    };

    // Helper function for headers
    const getHeaders = () => ({
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("token"),
    });

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        updateState({
            formData: {
                ...state.formData,
                [name]: value,
            },
        });
    };

    const handleSelectChange = (selectedOption) => {
        updateState({
            formData: {
                ...state.formData,
                courseId: selectedOption ? selectedOption.value : 0,
            },
        });
    };

    // Debounced search
    const debouncedSearch = useMemo(
        () =>
            debounce((query) => {
                updateState({ searchQuery: query, currentPage: 0 });
            }, 300),
        []
    );

    const handleSearchChange = (e) => {
        debouncedSearch(e.target.value);
    };

    const handleFilterChange = (e) => {
        updateState({
            filterStatus: e.target.value,
            currentPage: 0,
        });
    };

    const resetForm = () => {
        updateState({
            formData: {
                projectId: 0,
                courseId: 0,
                projectName: "",
                projectDescription: "",
                projectStatus: "Active",
                projectStartDate: "",
                projectEndDate: "",
            },
        });
    };

    const handleEdit = (project) => {
        updateState({
            formData: {
                ...project,
                projectStatus: project.projectStatus || "Active",
            },
        });
    };

    const handleDelete = async (projectId) => {
        const confirmation = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        });

        if (!confirmation.isConfirmed) return;

        try {
            await axios.post(
                `${API_LoginURL}project/DeleteProjectDetails`,
                { projectId },
                { headers: getHeaders() }
            );
            updateState({
                projects: state.projects.filter(
                    (item) => item.projectId !== projectId
                ),
            });
            Swal.fire("Deleted!", "Project has been deleted.", "success");
        } catch (error) {
            console.error("Error deleting project:", error);
            Swal.fire("Error", "Failed to delete project", "error");
        }
    };

    const handlePageChange = ({ selected }) => {
        updateState({ currentPage: selected });
    };

    const handleFormSubmit = async () => {
        updateState({ formSubmitting: true });
        try {
            const response = await axios.post(
                `${API_LoginURL}project/ProjectAddEdit`,
                state.formData,
                { headers: getHeaders() }
            );

            Swal.fire({
                icon:
                    response.data.response === "SUCCESS" ? "success" : "error",
                title:
                    response.data.response === "SUCCESS" ? "Success" : "Error",
                text:
                    response.data.response === "SUCCESS"
                        ? "Project saved successfully"
                        : response.data.response,
            });

            if (response.data.response === "SUCCESS") {
                fetchProjects();
                document.getElementById("closeProjectModal").click();
                resetForm();
            }
        } catch (error) {
            console.error("Error saving project:", error);
            Swal.fire("Error", "Failed to save project", "error");
        } finally {
            updateState({ formSubmitting: false });
        }
    };

    // Filter and pagination
    const filteredProjects = state.projects.filter((item) => {
        const matchesSearch =
            item.projectName
                .toLowerCase()
                .includes(state.searchQuery.toLowerCase()) ||
            item.projectDescription
                .toLowerCase()
                .includes(state.searchQuery.toLowerCase());

        const matchesStatus =
            state.filterStatus === "all" ||
            (state.filterStatus === "active" &&
                item.projectStatus === "Active") ||
            (state.filterStatus === "inactive" &&
                item.projectStatus === "Inactive") ||
            (state.filterStatus === "completed" &&
                item.projectStatus === "Completed") ||
            (state.filterStatus === "pending" &&
                item.projectStatus === "Pending");

        return matchesSearch && matchesStatus;
    });

    useEffect(() => {
        console.log("filteredProjects ====== ", filteredProjects);
    }, [state]);

    const pageCount = Math.ceil(filteredProjects.length / state.itemsPerPage);
    const displayedProjects = filteredProjects.slice(
        state.currentPage * state.itemsPerPage,
        (state.currentPage + 1) * state.itemsPerPage
    );

    // Get course name for display
    const getCourseName = (courseId) => {
        const course = state.courses.find((c) => c.courseId === courseId);
        return course ? course.courseName : "N/A";
    };

    return (
        <div className="container-scroller">
            <Navbar />
            <div className="container-fluid page-body-wrapper">
                <SASidebar />
                <div className="main-panel">
                    <div className="content-wrapper">
                        <div className="col-lg-12 grid-margin stretch-card">
                            <div className="card">
                                <div className="card-body">
                                    {/* Header Section */}
                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
                                        <h4 className="card-title mb-3 mb-md-0">
                                            Project Management
                                        </h4>

                                        {/* Search and Filter */}
                                        <div className="d-flex flex-column flex-md-row align-items-center gap-3 w-100 w-md-auto">
                                            {/* Search */}
                                            <div
                                                className="flex-grow-1 me-md-2 position-relative"
                                                style={{ maxWidth: "500px" }}
                                            >
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm ps-5 rounded-pill border-0"
                                                    placeholder="Search by Project Name or Description"
                                                    onChange={
                                                        handleSearchChange
                                                    }
                                                    style={{
                                                        height: "38px",
                                                        boxShadow:
                                                            "0 2px 8px rgba(0,0,0,0.08)",
                                                        transition:
                                                            "all 0.3s ease",
                                                        backgroundColor:
                                                            "#f8f9fa",
                                                    }}
                                                    onFocus={(e) => {
                                                        e.target.style.boxShadow =
                                                            "0 2px 12px rgba(0,0,0,0.15)";
                                                        e.target.style.backgroundColor =
                                                            "#fff";
                                                    }}
                                                    onBlur={(e) => {
                                                        e.target.style.boxShadow =
                                                            "0 2px 8px rgba(0,0,0,0.08)";
                                                        e.target.style.backgroundColor =
                                                            "#f8f9fa";
                                                    }}
                                                />
                                                <i className="fas fa-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                                            </div>

                                            {/* Filter */}
                                            <div className="d-flex align-items-center">
                                                <i className="fas fa-filter me-2 text-muted"></i>
                                                <select
                                                    className="form-select"
                                                    value={state.filterStatus}
                                                    onChange={
                                                        handleFilterChange
                                                    }
                                                    style={{
                                                        minWidth: "120px",
                                                    }}
                                                >
                                                    <option value="all">
                                                        All Status
                                                    </option>
                                                    <option value="active">
                                                        Active
                                                    </option>
                                                    <option value="inactive">
                                                        Inactive
                                                    </option>
                                                    <option value="completed">
                                                        Completed
                                                    </option>
                                                    <option value="pending">
                                                        Pending
                                                    </option>
                                                </select>
                                            </div>

                                            {/* Add Project Button */}
                                            <button
                                                className="btn btn-primary d-flex align-items-center"
                                                data-bs-toggle="modal"
                                                data-bs-target="#projectModal"
                                                onClick={resetForm}
                                            >
                                                <i className="fas fa-plus me-2"></i>
                                                <span className="d-none d-md-inline">
                                                    Add Project
                                                </span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Projects Table */}
                                    <div className="table-responsive">
                                        {state.loading ? (
                                            <div
                                                className="d-flex justify-content-center align-items-center"
                                                style={{ height: "200px" }}
                                            >
                                                <ClipLoader
                                                    color="#4e73df"
                                                    size={50}
                                                />
                                                <p className="mt-3 ms-3">
                                                    Loading projects...
                                                </p>
                                            </div>
                                        ) : (
                                            <>
                                                <table className="table table-hover table-striped">
                                                    <thead className="table-primary">
                                                        <tr>
                                                            <th width="10%">
                                                                ID
                                                            </th>
                                                            <th width="20%">
                                                                Course
                                                            </th>
                                                            <th width="20%">
                                                                Project Name
                                                            </th>
                                                            <th width="25%">
                                                                Description
                                                            </th>
                                                            <th width="15%">
                                                                Status
                                                            </th>
                                                            <th
                                                                width="10%"
                                                                className="text-end"
                                                            >
                                                                Actions
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {displayedProjects.length >
                                                        0 ? (
                                                            displayedProjects.map(
                                                                (item) => (
                                                                    <tr
                                                                        key={
                                                                            item.projectId
                                                                        }
                                                                    >
                                                                        <td>
                                                                            {
                                                                                item.projectId
                                                                            }
                                                                        </td>
                                                                        <td>
                                                                            {getCourseName(
                                                                                item.courseId
                                                                            )}
                                                                        </td>
                                                                        <td>
                                                                            {
                                                                                item.projectName
                                                                            }
                                                                        </td>
                                                                        <td>
                                                                            {
                                                                                item.projectDescription
                                                                            }
                                                                        </td>
                                                                        <td
                                                                            style={{
                                                                                color: item.projectStatus === "Completed" || item.projectStatus === "Active"
                                                                                  ? "green"
                                                                                  : item.projectStatus === "Pending"
                                                                                  ? "orange"
                                                                                  : "red",
                                                                              }}
                                                                        >
                                                                            {item.projectStatus
                                                                                ? item.projectStatus
                                                                                : "Inactive"}
                                                                        </td>
                                                                        <td className="text-end">
                                                                            <div className="d-flex justify-content-end gap-2">
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-primary"
                                                                                    data-bs-toggle="modal"
                                                                                    data-bs-target="#projectModal"
                                                                                    onClick={() =>
                                                                                        handleEdit(
                                                                                            item
                                                                                        )
                                                                                    }
                                                                                    title="Edit"
                                                                                >
                                                                                    <i className="fas fa-edit"></i>
                                                                                </button>
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-danger"
                                                                                    onClick={() =>
                                                                                        handleDelete(
                                                                                            item.projectId
                                                                                        )
                                                                                    }
                                                                                    title="Delete"
                                                                                >
                                                                                    <i className="fas fa-trash"></i>
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            )
                                                        ) : (
                                                            <tr>
                                                                <td
                                                                    colSpan="6"
                                                                    className="text-center py-4"
                                                                >
                                                                    <div className="d-flex flex-column align-items-center">
                                                                        <img
                                                                            src="https://img.icons8.com/ios/50/000000/no-camera--v1.png"
                                                                            alt="No projects"
                                                                            style={{
                                                                                opacity: 0.5,
                                                                                width: "60px",
                                                                            }}
                                                                        />
                                                                        <p className="mt-3 text-muted">
                                                                            No
                                                                            projects
                                                                            found
                                                                        </p>
                                                                        <button
                                                                            className="btn btn-sm btn-primary mt-2"
                                                                            data-bs-toggle="modal"
                                                                            data-bs-target="#projectModal"
                                                                            onClick={
                                                                                resetForm
                                                                            }
                                                                        >
                                                                            Add
                                                                            New
                                                                            Project
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>

                                                {/* Pagination */}
                                                {displayedProjects.length >
                                                    0 && (
                                                    <ReactPaginate
                                                        previousLabel={
                                                            <i className="fas fa-chevron-left"></i>
                                                        }
                                                        nextLabel={
                                                            <i className="fas fa-chevron-right"></i>
                                                        }
                                                        pageCount={pageCount}
                                                        onPageChange={
                                                            handlePageChange
                                                        }
                                                        containerClassName={
                                                            "pagination justify-content-center mt-4"
                                                        }
                                                        pageClassName={
                                                            "page-item"
                                                        }
                                                        pageLinkClassName={
                                                            "page-link"
                                                        }
                                                        previousClassName={
                                                            "page-item"
                                                        }
                                                        previousLinkClassName={
                                                            "page-link"
                                                        }
                                                        nextClassName={
                                                            "page-item"
                                                        }
                                                        nextLinkClassName={
                                                            "page-link"
                                                        }
                                                        activeClassName={
                                                            "active"
                                                        }
                                                        breakLabel={"..."}
                                                        marginPagesDisplayed={2}
                                                        pageRangeDisplayed={5}
                                                        forcePage={
                                                            state.currentPage
                                                        }
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Project Modal */}
            <div
                className="modal fade"
                id="projectModal"
                tabIndex="-1"
                aria-labelledby="projectModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header bg-light">
                            <h5 className="modal-title" id="projectModalLabel">
                                {state.formData.projectId > 0
                                    ? "Edit Project"
                                    : "Add New Project"}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            <form>
                                <div className="row">
                                    <div className="col-md-6 mb-3 pe-0">
                                        <label
                                            htmlFor="courseId"
                                            className="form-label required"
                                        >
                                            Course
                                        </label>
                                        {/* <Select
                                            id="courseId"
                                            options={state.courses.map(
                                                (course) => ({
                                                    value: course.courseId,
                                                    label: course.courseName,
                                                })
                                            )}
                                            value={
                                                state.courses.find(
                                                    (option) =>
                                                        option.courseId ===
                                                        state.formData.courseId
                                                ) || null
                                            }
                                            onChange={handleSelectChange}
                                            placeholder="Select Course"
                                            className="basic-single"
                                            classNamePrefix="select"
                                            styles={{
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color: "black",
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color: "gray",
                                                }),
                                            }}
                                            isClearable
                                            isSearchable
                                        /> */}

                                        <select
                                            className="form-select"
                                            id="courseId"
                                            name="courseId"
                                            value={state.formData.courseId}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="">
                                                Select Course
                                            </option>
                                            {state.courses.map((c) => (
                                                <option key={c.courseId} value={c.courseId}>
                                                    {c.courseName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-5 mb-3 ps-0 flex-grow-1">
                                        <label
                                            htmlFor="projectName"
                                            className="form-label required"
                                        >
                                            Project Name
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control p-2"
                                            id="projectName"
                                            name="projectName"
                                            value={state.formData.projectName}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                <div className="mb-3">
                                    <label
                                        htmlFor="projectDescription"
                                        className="form-label"
                                    >
                                        Description
                                    </label>
                                    <textarea
                                        className="form-control"
                                        id="projectDescription"
                                        name="projectDescription"
                                        rows="2"
                                        value={
                                            state.formData.projectDescription
                                        }
                                        onChange={handleInputChange}
                                    />
                                </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6 mb-3 pe-0">
                                        <label
                                            htmlFor="projectStartDate"
                                            className="form-label"
                                        >
                                            Start Date
                                        </label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            id="projectStartDate"
                                            name="projectStartDate"
                                            value={state.formData.projectStartDate ? state.formData.projectStartDate.split("T")[0] : ""}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="col-md-5 mb-3 flex-grow-1 ps-0">
                                        <label
                                            htmlFor="projectEndDate"
                                            className="form-label"
                                        >
                                            End Date
                                        </label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            id="projectEndDate"
                                            name="projectEndDate"
                                            value={state.formData.projectEndDate ? state.formData.projectEndDate.split("T")[0] : ""}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                <div className="col-md-6 mb-3 pe-0">
                                    <label
                                        htmlFor="projectStatus"
                                        className="form-label"
                                    >
                                        Status
                                    </label>
                                    <select
                                        className="form-select"
                                        id="projectStatus"
                                        name="projectStatus"
                                        value={state.formData.projectStatus}
                                        onChange={handleInputChange}
                                    >
                                        <option value="Active">Active</option>
                                        <option value="Inactive">
                                            Inactive
                                        </option>
                                        <option value="Completed">
                                            Completed
                                        </option>
                                        <option value="Pending">Pending</option>
                                    </select>
                                </div>
                                </div>
                            </form>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                id="closeProjectModal"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleFormSubmit}
                                disabled={state.formSubmitting}
                            >
                                {state.formSubmitting ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Submitting...
                                    </>
                                ) : (
                                    "Save Changes"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
